import type { BuilderTextFormatFlags } from './types'

/**
 * Whole-field bold/italic for question prompt + description: plain text in the builder,
 * composed markdown on draft/API (`**…**`, `*…*`, `***…***`).
 */

export const defaultBuilderTextFormatFlags: BuilderTextFormatFlags = Object.freeze({
  bold: false,
  italic: false,
})

function stripOuterTriple(s: string): { inner: string } | null {
  const t = s.trim()
  if (t.length < 6 || !t.startsWith('***') || !t.endsWith('***')) return null
  const inner = t.slice(3, -3)
  if (inner.includes('*')) return null
  return { inner }
}

function stripOuterBold(s: string): { inner: string } | null {
  const t = s.trim()
  if (t.startsWith('***')) return null
  if (t.length < 4 || !t.startsWith('**') || !t.endsWith('**')) return null
  const inner = t.slice(2, -2)
  if (inner.includes('*')) return null
  return { inner }
}

function stripOuterItalic(s: string): { inner: string } | null {
  const t = s.trim()
  if (t.startsWith('**')) return null
  if (t.length < 2 || !t.startsWith('*') || !t.endsWith('*')) return null
  const inner = t.slice(1, -1)
  if (inner.length === 0 || inner.includes('*')) return null
  return { inner }
}

/** Split stored prompt/description into plain editor text + whole-field format flags. */
export function splitWholeFieldMarkdown(stored: string): { plain: string; fmt: BuilderTextFormatFlags } {
  const triple = stripOuterTriple(stored)
  if (triple) return { plain: triple.inner, fmt: { bold: true, italic: true } }
  const bold = stripOuterBold(stored)
  if (bold) return { plain: bold.inner, fmt: { bold: true, italic: false } }
  const italic = stripOuterItalic(stored)
  if (italic) return { plain: italic.inner, fmt: { bold: false, italic: true } }
  return { plain: stored, fmt: { ...defaultBuilderTextFormatFlags } }
}

/** Compose API/draft markdown from plain builder text + flags. */
export function composeWholeFieldMarkdown(plain: string, fmt: BuilderTextFormatFlags): string {
  const core = plain
  if (!core) return ''
  if (fmt.bold && fmt.italic) return `***${core}***`
  if (fmt.bold) return `**${core}**`
  if (fmt.italic) return `*${core}*`
  return core
}
